import type { MenuItemDto } from '../../tiposGlobais/menu'

/** Remove barra final e espaços para comparar rotas de forma estável. */
export function normalizarCaminho(p: string): string {
  const t = p.trim()
  if (t.length > 1 && t.endsWith('/'))
    return t.slice(0, -1)
  return t
}

/** Rotas onde não há “ancestral” na árvore (/dashboard casa com item raíz), mas o usuário ainda pode usar flyout rail (ex.: abrir Projetos no Dashboard). */
export function rotaEhRaizOuDashboard(caminho: string): boolean {
  const n = normalizarCaminho(caminho)
  return n === '' || n === '/' || n === '/dashboard'
}

/**
 * A rota cadastrada no item cobre a URL atual: igualdade ou a URL é subcaminho (ex.: `/projetos/foo` sob `/projetos`).
 * Não usa prefixo ambíguo: exige `/` após o prefixo quando for extensão.
 */
export function rotaMenuCobreDestino(rotaItem: string | undefined, caminhoDestino: string): boolean {
  const r = (rotaItem ?? '').trim()
  if (r === '')
    return false
  const d = normalizarCaminho(caminhoDestino)
  const rn = normalizarCaminho(r)
  if (d === rn)
    return true
  return d.startsWith(`${rn}/`)
}

/**
 * Igual ao comportamento original, com paths normalizados (barra final, etc.).
 */
function coletarAncestraisExato(items: MenuItemDto[], caminhoDestino: string): number[] | null {
  const destino = normalizarCaminho(caminhoDestino)
  for (const item of items)
  {
    const rota = item.rota?.trim()
    if (rota && normalizarCaminho(rota) === destino)
      return []
    if (item.filhos && item.filhos.length > 0)
    {
      const filhos = coletarAncestraisExato(item.filhos, caminhoDestino)
      if (filhos !== null)
        return [item.codigo, ...filhos]
    }
  }
  return null
}

interface CadeiaCandidata {
  cadeia: MenuItemDto[]
}

function coletarCadeiasQueCobremDestino(items: MenuItemDto[], destino: string, prefixo: MenuItemDto[]): CadeiaCandidata[] {
  const saida: CadeiaCandidata[] = []
  for (const item of items)
  {
    const cadeia = [...prefixo, item]
    const r = item.rota?.trim()
    if (r && rotaMenuCobreDestino(r, destino))
      saida.push({ cadeia })
    if (item.filhos && item.filhos.length > 0)
      saida.push(...coletarCadeiasQueCobremDestino(item.filhos, destino, cadeia))
  }
  return saida
}

/**
 * Quando não há nó com rota exata na árvore (ex.: filho removido por permissão, URL mais profunda que a folha do menu),
 * usa o item cujo `rota` é o prefixo mais longo que ainda cobre `caminhoDestino`.
 * Ancestrais = códigos do caminho até o pai do nó escolhido (igual ao caso exato).
 */
function coletarAncestraisPorPrefixoMaisLongo(items: MenuItemDto[], caminhoDestino: string): number[] | null {
  const candidatos = coletarCadeiasQueCobremDestino(items, caminhoDestino, [])
  if (candidatos.length === 0)
    return null
  candidatos.sort((a, b) => {
    const folhaA = a.cadeia[a.cadeia.length - 1]
    const folhaB = b.cadeia[b.cadeia.length - 1]
    const la = normalizarCaminho(folhaA.rota ?? '').length
    const lb = normalizarCaminho(folhaB.rota ?? '').length
    if (lb !== la)
      return lb - la
    return b.cadeia.length - a.cadeia.length
  })
  const melhor = candidatos[0].cadeia
  if (melhor.length <= 1)
    return []
  return melhor.slice(0, -1).map((n) => n.codigo)
}

/**
 * Cadeia de códigos do item raiz até o ancestral do destino ([raiz], [raiz, …]).
 * Vazio quando a própria rota é topo.
 * Inclui fallback por prefixo quando não há folha exata na árvore (permissões / rotas dinâmicas).
 */
export function coletarAncestraisMenuPorRota(items: MenuItemDto[], caminhoDestino: string): number[] | null {
  const exato = coletarAncestraisExato(items, caminhoDestino)
  if (exato !== null)
    return exato
  return coletarAncestraisPorPrefixoMaisLongo(items, caminhoDestino)
}
